import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import { Field, reduxForm } from 'redux-form';
import classnames from 'classnames';

import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import ServiceIcon from 'components/ServiceIcon/ServiceIcon';
import { required, maxLength } from 'services/validationMsgs';
import RocketChip from 'components/MissionCtrl/RocketChip/RocketChip';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';

const maxLength128 = maxLength(128);

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  newInstanceName: PropTypes.string.isRequired,
  newInstanceService: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    maturityLevel: PropTypes.number,
  }).isRequired,
  newInstanceServiceSubtype: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  newInstanceVersion: PropTypes.string.isRequired,
  newInstanceRegion: PropTypes.string.isRequired,
  newInstanceCloudProvider: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  selectService: PropTypes.func.isRequired,
  selectServiceSubtype: PropTypes.func.isRequired,
  selectProvider: PropTypes.func.isRequired,
  selectVersion: PropTypes.func.isRequired,
  selectRegion: PropTypes.func.isRequired,
  enterName: PropTypes.func.isRequired,
  services: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  serviceSubtypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  cloudProviders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  versions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialize: PropTypes.func.isRequired,
  isORv4User: PropTypes.bool.isRequired,
  trackButtonClick: PropTypes.func.isRequired,
  validRegions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    organizationId: PropTypes.string,
    ORv4ID: PropTypes.string,
  }).isRequired,
};

export function UnstyledStepOne({
  newInstanceName,
  newInstanceService,
  newInstanceServiceSubtype,
  newInstanceVersion,
  newInstanceRegion,
  newInstanceCloudProvider,
  selectService,
  selectVersion,
  selectRegion,
  enterName,
  selectProvider,
  classes,
  services,
  serviceSubtypes,
  cloudProviders,
  versions,
  selectServiceSubtype,
  initialize,
  isORv4User,
  trackButtonClick,
  validRegions,
  user,
}) {
  const isStateEmpty = (state) => !state || Object.keys(state).length === 0;

  const currentVersions = !isStateEmpty(newInstanceService)
    ? versions
        .filter(
          (catalogVersion) =>
            catalogVersion.serviceSubtype === newInstanceServiceSubtype.id &&
            catalogVersion.displaySetting === 1,
        )
        .map((catalogVersion) => (
          <MenuItem key={catalogVersion.id} value={catalogVersion.name}>
            {catalogVersion.name}
          </MenuItem>
        ))
    : [];

  const handleWidgetClick = (requestSource) => {
    // init user specs
    const { id, email, organizationId, ORv4ID } = user;
    // init realtime instance spec. selections
    const instanceName = newInstanceName || '';
    const instanceService = newInstanceService.name || '';
    const instanceVersion = newInstanceVersion || '';
    const instanceCloudProvider = newInstanceCloudProvider.name || '';
    const requestType = requestSource || '';
    // eslint-disable-next-line no-underscore-dangle
    const chatlioWidget = window._chatlio;

    // hide the widget if it's shown
    if (chatlioWidget.isShown()) {
      chatlioWidget.hide();
      chatlioWidget.reset();
    } else {
      // identify the user
      chatlioWidget.identify(id, {
        email,
        organizationId,
        ORv4ID,
        instanceName,
        instanceService,
        instanceVersion,
        instanceCloudProvider,
        requestType,
      });
      // configure the widget
      chatlioWidget.configure({
        status: 'offline',
        offlineGreeting: 'Looking for a new region? Let us know!',
        offlineMessagePlaceholder: 'Where would you like to host your data?',
      });
      chatlioWidget.show({ expanded: true });
    }
  };

  const regionOptions = !isStateEmpty(newInstanceService)
    ? validRegions.map((currentRegion) => (
        <MenuItem key={currentRegion.id} value={currentRegion.name}>
          {currentRegion.name}
        </MenuItem>
      ))
    : null;

  const currentSubtypes = {};

  const isRedisInCatalog =
    services.filter((service) => service.name === 'Redis' && service.displaySetting === 1).length >
    0;

  const servicesOnlyInORv4 = isRedisInCatalog ? ['MongoDB'] : ['MongoDB', 'Redis'];
  const orv4Chips = isORv4User
    ? servicesOnlyInORv4.map((orv4Service) => (
        <SegmentClickTracker key={orv4Service} buttonName={orv4Service.concat(' SSO')}>
          <RocketChip
            component="a"
            href={process.env.REACT_APP_ORV4_SSO_LOGIN}
            target="_blank"
            rel="noopener noreferrer"
            key={orv4Service}
            label={orv4Service}
            icon={<ServiceIcon service={orv4Service} isAlpha={false} />}
            onClick={() => {}}
            isORv4
            isSelected={false}
          />
        </SegmentClickTracker>
      ))
    : [];

  const clickService = (event, catalogService) => {
    selectService(
      event,
      catalogService,
      currentSubtypes[catalogService.id][0],
      newInstanceCloudProvider.id ? newInstanceCloudProvider : cloudProviders[0],
    );

    if (newInstanceCloudProvider.name === 'GCP' && catalogService.name === 'CockroachDB') {
      selectProvider({}, cloudProviders[0]);
    }
  };

  const serviceChips = !isStateEmpty(services)
    ? services
        .filter((service) => service.displaySetting === 1)
        .map((catalogService) => {
          currentSubtypes[catalogService.id] = serviceSubtypes.filter(
            (catalogServiceSubtype) =>
              catalogServiceSubtype.service === catalogService.id &&
              catalogServiceSubtype.displaySetting === 1,
          );
          return (
            <SegmentClickTracker key={catalogService.id} buttonName={catalogService.name}>
              <RocketChip
                key={catalogService.id}
                label={catalogService.name}
                onClick={(event) => clickService(event, catalogService)}
                isSelected={catalogService.name === newInstanceService.name}
                icon={
                  <ServiceIcon
                    service={catalogService.name}
                    isAlpha={catalogService.maturityLevel === 1}
                  />
                }
                clickable
              />
            </SegmentClickTracker>
          );
        })
    : null;

  const allServiceChips = !isStateEmpty(services) ? [...serviceChips, ...orv4Chips] : null;

  const serviceSubtypeChips = !isStateEmpty(newInstanceService)
    ? currentSubtypes[newInstanceService.id].map((catalogServiceSubtype) => (
        <SegmentClickTracker
          key={catalogServiceSubtype.id}
          buttonName={newInstanceService.name.concat(' ', catalogServiceSubtype.name.toUpperCase())}
        >
          <RocketChip
            key={catalogServiceSubtype.id}
            label={catalogServiceSubtype.name}
            onClick={(event) => selectServiceSubtype(event, catalogServiceSubtype)}
            isSelected={catalogServiceSubtype.id === newInstanceServiceSubtype.id}
          />
        </SegmentClickTracker>
      ))
    : null;

  let visibleCloudProviders = cloudProviders;

  if (newInstanceService.name === 'CockroachDB') {
    visibleCloudProviders = cloudProviders.filter((cloudProvider) => cloudProvider.name !== 'GCP');
  }

  const orv5cloudProviderChips = visibleCloudProviders.map((catalogCloudProvider) => (
    // eslint-disable-next-line max-len
    <SegmentClickTracker
      key={catalogCloudProvider.id}
      buttonName={catalogCloudProvider.name.toUpperCase()}
    >
      <RocketChip
        key={catalogCloudProvider.id}
        label={catalogCloudProvider.name}
        onClick={(event) => selectProvider(event, catalogCloudProvider)}
        isSelected={catalogCloudProvider.id === newInstanceCloudProvider.id}
        className={classnames(
          classes.serviceSubtype,
          catalogCloudProvider.id === newInstanceCloudProvider.id
            ? classes.selectedServiceSubtype
            : null,
        )}
      />
    </SegmentClickTracker>
  ));

  const orv4CloudProviders = ['Rackspace'];
  const servicesAlsoInORv4 = ['Redis', 'Elasticsearch'];
  const orv4CloudProviderChips = servicesAlsoInORv4.includes(newInstanceService.name)
    ? orv4CloudProviders.map((orv4CloudProvider) => (
        <SegmentClickTracker key={orv4CloudProvider} buttonName={orv4CloudProvider.concat(' SSO')}>
          <RocketChip
            component="a"
            href={process.env.REACT_APP_ORV4_SSO_LOGIN}
            target="_blank"
            rel="noopener noreferrer"
            key={orv4CloudProvider}
            label={orv4CloudProvider}
            onClick={() => {}}
            isORv4
            isSelected={false}
          />
        </SegmentClickTracker>
      ))
    : [];

  const cloudProviderChips = [...orv5cloudProviderChips, ...orv4CloudProviderChips];

  React.useEffect(() => {
    initialize({ name: newInstanceName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <div className={classes.container}>
        <div className={classes.form}>
          <Field
            name="name"
            label="Name"
            variant="outlined"
            placeholder="What do you want to name your instance?"
            validate={[required, maxLength128]}
            onChange={enterName}
            component={WrappedTextField}
            props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
            className={classes.nameField}
            fullWidth
            autoFocus
          />

          <div className={classes.servicesContainer}>
            <Typography className={classes.chipSectionLabel} component="p">
              Select Your Service:
            </Typography>
            <div className={classes.serviceChipContainer}>{allServiceChips}</div>
          </div>

          {!isStateEmpty(newInstanceService) ? (
            <div className={classes.cloudAndServiceContainer}>
              <div className={classes.cloudContainer}>
                <Typography className={classes.chipSectionLabel} component="p">
                  Cloud Provider:
                </Typography>
                {cloudProviderChips}
              </div>

              <div className={classes.typeContainer}>
                <Typography className={classes.chipSectionLabel} component="p">
                  Type:
                </Typography>
                {serviceSubtypeChips}
              </div>
            </div>
          ) : null}

          <div className={classes.selectionContainer}>
            <Field
              classes={{ root: classes.selectionFormRoot }}
              variant="outlined"
              name="versions"
              label="Version"
              component={WrappedTextField}
              disabled={currentVersions.length === 0}
              onChange={(e) => {
                selectVersion(e);
                trackButtonClick(
                  newInstanceService.name.concat(' ', e.target.value.toUpperCase(), ' Version'),
                );
              }}
              select
              validate={[maxLength128, required]}
              InputLabelProps={{
                shrink: true,
                className: classes.inputLabel,
              }}
              SelectProps={{
                displayEmpty: true,
                value: newInstanceVersion,
                classes: { root: classes.selectRoot },
              }}
              className={classes.state}
            >
              <MenuItem value="0" disabled>
                Select Your Version
              </MenuItem>
              {currentVersions}
            </Field>
            <div className={classes.selectionFormRootSubText}>
              <Field
                classes={{ root: classes.selectionFormRoot }}
                variant="outlined"
                name="regions"
                label="Region"
                component={WrappedTextField}
                disabled={isStateEmpty(newInstanceService)}
                onChange={(e) => {
                  selectRegion(e);
                  trackButtonClick(
                    newInstanceService.name.concat(' ', e.target.value.toUpperCase(), ' Region'),
                  );
                }}
                select
                validate={[maxLength128, required]}
                InputLabelProps={{
                  shrink: true,
                  className: classes.inputLabel,
                }}
                SelectProps={{
                  displayEmpty: true,
                  value: newInstanceRegion,
                  classes: { root: classes.selectRoot },
                }}
                className={classes.state}
              >
                <MenuItem value="0" disabled>
                  Select Your Region
                </MenuItem>
                {regionOptions}
              </Field>
              <Typography
                name="regionRequest"
                className={classes.widgetButtonLabel}
                onClick={() => handleWidgetClick('Request Region')}
              >
                Request Another Region
              </Typography>
            </div>
          </div>
        </div>
        <Paper className={classes.infoBox} elevation={0}>
          <Typography component="div">
            <div>
              <p>
                Configure your ObjectRocket datastore instance and receive best-in-class service.
              </p>
            </div>
            {(newInstanceService.maturityLevel === 1 || newInstanceService.maturityLevel === 2) && (
              <div>
                <p>
                  <strong>
                    Our &nbsp;
                    {newInstanceService.name}
                    &nbsp; service is currently in &nbsp;
                    {newInstanceService.maturityLevel === 1 ? 'alpha' : 'beta'}.
                  </strong>
                </p>
              </div>
            )}
            {newInstanceService.maturityLevel === 1 && (
              <div>
                <p>
                  ObjectRocket Alpha products are subject to our &nbsp;
                  <a
                    href="https://www.rackspace.com/information/legal/testterms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Test Terms
                  </a>
                  &nbsp; and do not adhere to ObjectRocket support SLAs. Alpha products are for
                  evaluation and testing purposes only and carry a limited feature set.
                </p>
                <p>
                  Features like HA, backups, security, and ObjectRocket support are not guaranteed
                  on all services while in Alpha.
                </p>
              </div>
            )}
            {newInstanceService.maturityLevel === 2 && (
              <div>
                <p>
                  ObjectRocket Beta products are subject to our &nbsp;
                  <a
                    href="https://www.rackspace.com/information/legal/testterms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Test Terms
                  </a>
                  &nbsp; and do not adhere to ObjectRocket support SLAs. Therefore we strongly
                  advise against using Beta services in any production environment.
                </p>
                <p>
                  Features like HA, backups, and ObjectRocket support are not guaranteed on all
                  services while in Beta.
                </p>
              </div>
            )}
            {newInstanceService.maturityLevel === 3 && (
              <div>
                All ObjectRocket services include:
                <ul>
                  <li>HA clusters</li>
                  <li>Dedicated containers</li>
                  <li>
                    Security via IP whitelists, encryption in transit, encryption at rest, and user
                    auth
                  </li>
                  <li>24x7 ObjectRocket support</li>
                </ul>
              </div>
            )}
            <div>
              <p>
                Don’t see what you’re looking for? ObjectRocket offers a number of customer options
                and features, so contact &nbsp;
                <a href="mailto:sales@objectrocket.com">sales@objectrocket.com</a>
                &nbsp; for more information
              </p>
            </div>
          </Typography>
        </Paper>
      </div>
    </React.Fragment>
  );
}

UnstyledStepOne.propTypes = propTypes;

const styles = (theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '1rem',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      marginBottom: 0,
    },
  },
  form: {
    flex: '0 0 60%',
    '& > *': {
      marginTop: theme.spacing(1),
    },
  },
  nameField: {
    width: '100%',

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      '& input': {
        fontSize: '4.5vw',
      },
    },
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
  chipSectionLabel: {
    fontSize: 12,
  },
  widgetButtonLabel: {
    align: 'left',
    color: '#0000EE',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontStyle: 'italic',
    fontSize: '11px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
    width: 'max-content',
  },
  selectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2.5),

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      width: '100%',
    },
  },
  selectionFormRoot: {
    flex: '0 0 49%',
  },
  selectionFormRootSubText: {
    flex: '0 0 49%',
    display: 'grid',
  },
  selectRoot: {
    '&:focus': {
      backgroundColor: 'unset',
    },
  },
  serviceChipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: theme.spacing(1),

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      rowGap: `${theme.spacing(1)}px`,
    },
  },
  serviceSubtype: {
    justifyContent: 'space-between',
    backgroundColor: theme.customColors.lightGrey,
    marginTop: theme.spacing(1),
    '&:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
    '&:hover': {
      backgroundColor: theme.customColors.lightGrey,
    },
    '&:active, &:focus': {
      backgroundColor: theme.customColors.peach,
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  selectedServiceSubtype: {
    backgroundColor: theme.customColors.peach,
  },
  infoBox: {
    flex: '0 0 27%',
    alignSelf: 'flex-start',
    marginTop: '2rem',
    marginRight: '2rem',
    borderLeft: `1px solid ${theme.customColors.mediumGrey}`,
    borderRadius: 'unset',
    fontStyle: 'italic',
    paddingLeft: theme.spacing(1),
    color: theme.customColors.mediumGrey,
    fontSize: '.9rem',
    '& div': {
      color: 'unset',
      fontSize: 'unset',
    },

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  cloudAndServiceContainer: {
    display: 'flex',

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  cloudContainer: {
    marginRight: theme.spacing(2),

    [theme.breakpoints.down('xs')]: {
      marginRight: 'unset',
    },
  },
  listItemRoot: {
    padding: 'unset',
  },
  listItemIconRoot: {
    alignSelf: 'flex-start',
    marginRight: '.2rem',
    marginTop: '.375rem',
  },
  listBullet: {
    // Using px instead of rem because Chrome doesn't display
    // this icon properly at rems less than one. It doesn't scale,
    // but it still looks ok at larger font sizes.
    fontSize: '4px',
  },
  rocketContainer: {
    marginTop: '2rem',
  },
});

export default withStyles(styles)(reduxForm({ form: 'newInstanceName' })(UnstyledStepOne));
